if ( !customElements.get( 'kids-product-addons' ) ) {
	customElements.define(
		'kids-product-addons',
		class KidsProductAddons extends HTMLElement {
			constructor () {
				super();
				this._onFormSubmit = this._onFormSubmit.bind( this );
				this._onAddonChange = this._onAddonChange.bind( this );
				this._onCartChange = this._onCartChange.bind( this );
				this._onCartRefresh = this._onCartRefresh.bind( this );
				this._form = null;
			}

			connectedCallback () {
				this._form = this._resolveProductForm();
				document.addEventListener( 'submit', this._onFormSubmit, true );
				this.addEventListener( 'change', this._onAddonChange );
				document.addEventListener( 'cart:change', this._onCartChange );
				document.addEventListener( 'cart:refresh', this._onCartRefresh );
				this._restoreStoredSelection();
				this._syncParentLiquidBlockVisibility();
			}

			disconnectedCallback () {
				document.removeEventListener( 'submit', this._onFormSubmit, true );
				this.removeEventListener( 'change', this._onAddonChange );
				document.removeEventListener( 'cart:change', this._onCartChange );
				document.removeEventListener( 'cart:refresh', this._onCartRefresh );
			}

			_resolveProductForm () {
				const productRoot = this.closest( 'product-rerender' ) || this.closest( 'section' ) || this.parentElement;
				if ( !productRoot ) return null;

				return productRoot.querySelector( 'form[action*="/cart/add"]' );
			}

			async _onFormSubmit ( event ) {
				const form = event.target;
				if ( !( form instanceof HTMLFormElement ) ) return;
				if ( !form.matches( 'form[action*="/cart/add"]' ) ) return;
				if ( event.defaultPrevented ) return;

				if ( !this._form || !document.contains( this._form ) ) {
					this._form = this._resolveProductForm();
				}

				if ( this._form && form !== this._form ) return;

				const selectedAddons = this._getSelectedAddons();
				if ( selectedAddons.length === 0 ) return;

				event.preventDefault();
				event.stopPropagation();
				if ( typeof event.stopImmediatePropagation === 'function' ) {
					event.stopImmediatePropagation();
				}

				if ( event.submitter?.getAttribute( 'aria-busy' ) === 'true' ) {
					return;
				}

				if ( !form.checkValidity() ) {
					form.reportValidity();
					return;
				}

				const submitButtons = Array.from( form.elements ).filter( ( element ) => element.type === 'submit' );
				submitButtons.forEach( ( submitButton ) => submitButton.setAttribute( 'aria-busy', 'true' ) );

				const showLoadingBar = event.submitter?.querySelector( 'button-content' ) === null;
				if ( showLoadingBar ) {
					document.documentElement.dispatchEvent( new CustomEvent( 'theme:loading:start', { bubbles: true } ) );
				}

				try {
					const sectionsToBundle = [ 'variant-added' ];
					document.documentElement.dispatchEvent( new CustomEvent( 'cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } } ) );

					const formData = new FormData( form );
					const mainItem = this._extractMainItem( formData );

					if ( !mainItem ) {
						throw new Error( 'No se ha podido determinar la variante seleccionada.' );
					}

					const nestedAddons = selectedAddons.map( ( addon ) => ( {
						...addon,
						parent_id: mainItem.id
					} ) );
					const items = [ mainItem, ...nestedAddons ];
					const payload = { items };

					if ( sectionsToBundle.length > 0 ) {
						payload.sections = sectionsToBundle.join( ',' );
					}
					
					const response = await fetch( `${ window.Shopify.routes.root }cart/add.js`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							'X-Requested-With': 'XMLHttpRequest'
						},
						body: JSON.stringify( payload )
					} );

					const responseJson = await response.json();

					if ( !response.ok ) {
						throw responseJson;
					}

					if ( window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart' ) {
						window.location.href = `${ window.Shopify.routes.root }cart`;
						return;
					}

					const cartContent = await ( await fetch( `${ window.Shopify.routes.root }cart.js` ) ).json();
					cartContent.sections = responseJson.sections || await this._fetchSections( sectionsToBundle );

					const addedItems = responseJson.hasOwnProperty( 'items' ) ? responseJson.items : [ responseJson ];

					form.dispatchEvent( new CustomEvent( 'variant:add', {
						bubbles: true,
						detail: {
							items: addedItems,
							cart: cartContent
						}
					} ) );

					document.documentElement.dispatchEvent( new CustomEvent( 'cart:change', {
						bubbles: true,
						detail: {
							baseEvent: 'variant:add',
							cart: cartContent
						}
					} ) );
				}
				catch ( error ) {
					const message = error?.message || error?.description || 'No se pudo agregar al carrito';

					form.dispatchEvent( new CustomEvent( 'cart:error', {
						bubbles: true,
						detail: {
							error: message
						}
					} ) );

					document.dispatchEvent( new CustomEvent( 'toast:show', {
						detail: {
							message,
							tone: 'error'
						}
					} ) );

					document.documentElement.dispatchEvent( new CustomEvent( 'cart:refresh', { bubbles: true } ) );
				}
				finally {
					submitButtons.forEach( ( submitButton ) => submitButton.removeAttribute( 'aria-busy' ) );

					if ( showLoadingBar ) {
						document.documentElement.dispatchEvent( new CustomEvent( 'theme:loading:end', { bubbles: true } ) );
					}
				}
			}

			_extractMainItem ( formData ) {
				const id = parseInt( formData.get( 'id' ), 10 );
				if ( Number.isNaN( id ) ) return null;

				const quantity = parseInt( formData.get( 'quantity' ), 10 );
				const item = {
					id,
					quantity: Number.isNaN( quantity ) || quantity < 1 ? 1 : quantity
				};

				const sellingPlan = formData.get( 'selling_plan' );
				if ( sellingPlan ) {
					item.selling_plan = sellingPlan;
				}

				const properties = {};
				formData.forEach( ( value, key ) => {
					if ( key.startsWith( 'properties[' ) && key.endsWith( ']' ) ) {
						const propertyKey = key.slice( 11, -1 );
						if ( propertyKey ) {
							properties[ propertyKey ] = value;
						}
					}
				} );

				if ( Object.keys( properties ).length > 0 ) {
					item.properties = properties;
				}

				return item;
			}

			_getSelectedAddons () {
				const selected = new Map();
				const items = this.querySelectorAll( 'kids-product-addon' );

				items.forEach( ( item ) => {
					if ( item.hidden ) return;

					const checkbox = item.querySelector( 'input[type="checkbox"]' );

					if ( checkbox && checkbox.checked ) {
						const variantId = parseInt( item.dataset.variantId, 10 );
						const quantity = parseInt( item.dataset.quantity, 10 );

						if ( !Number.isNaN( variantId ) ) {
							const previousQuantity = selected.get( variantId ) || 0;
							selected.set(
								variantId,
								previousQuantity + ( Number.isNaN( quantity ) || quantity < 1 ? 1 : quantity )
							);
						}
					}
				} );

				return Array.from( selected.entries() ).map(
					( [ id, quantity ] ) => ( { id, quantity } )
				);
			}

			_onAddonChange ( event ) {
				const target = event.target;
				if ( !( target instanceof HTMLInputElement ) ) return;
				if ( target.type !== 'checkbox' ) return;

				this._persistSelection();
			}

			_onCartChange ( event ) {
				const cart = event?.detail?.cart;
				if ( !cart ) return;

				this._syncAddonsVisibilityWithCart( cart );
			}

			async _onCartRefresh () {
				try {
					const cart = await ( await fetch( `${ window.Shopify.routes.root }cart.js` ) ).json();
					this._syncAddonsVisibilityWithCart( cart );
				}
				catch ( error ) {}
			}

			_getStorageKey () {
				return `kids:addons:${ window.location.pathname }`;
			}

			_readStoredSelection () {
				try {
					const raw = window.localStorage.getItem( this._getStorageKey() );
					if ( !raw ) return [];

					const parsed = JSON.parse( raw );
					if ( !Array.isArray( parsed ) ) return [];

					return parsed
						.map( ( value ) => parseInt( value, 10 ) )
						.filter( ( value ) => !Number.isNaN( value ) );
				}
				catch ( error ) {
					return [];
				}
			}

			_restoreStoredSelection () {
				const stored = new Set( this._readStoredSelection() );
				if ( stored.size === 0 ) return;

				this.querySelectorAll( 'kids-product-addon' ).forEach( ( item ) => {
					const checkbox = item.querySelector( 'input[type="checkbox"]' );
					const productId = parseInt( item.dataset.productId, 10 );

					if ( !checkbox || Number.isNaN( productId ) ) return;
					if ( item.hidden ) {
						checkbox.checked = false;
						return;
					}
					if ( checkbox.disabled ) {
						checkbox.checked = true;
						return;
					}
					checkbox.checked = stored.has( productId );
				} );
			}

			_persistSelection () {
				try {
					const selected = Array.from( this.querySelectorAll( 'kids-product-addon' ) )
						.filter( ( item ) => !item.hidden )
						.filter( ( item ) => item.querySelector( 'input[type="checkbox"]' )?.checked )
						.map( ( item ) => parseInt( item.dataset.productId, 10 ) )
						.filter( ( productId ) => !Number.isNaN( productId ) );

					if ( selected.length === 0 ) {
						window.localStorage.removeItem( this._getStorageKey() );
						return;
					}

					window.localStorage.setItem( this._getStorageKey(), JSON.stringify( selected ) );
				}
				catch ( error ) {}
			}

			_syncAddonsVisibilityWithCart ( cart ) {
				const items = Array.isArray( cart?.items ) ? cart.items : [];
				const variantsInCart = new Set(
					items
						.map( ( item ) => parseInt( item?.variant_id ?? item?.id, 10 ) )
						.filter( ( variantId ) => !Number.isNaN( variantId ) )
				);

				this.querySelectorAll( 'kids-product-addon' ).forEach( ( item ) => {
					const variantId = parseInt( item.dataset.variantId, 10 );
					if ( Number.isNaN( variantId ) ) return;

					const isInCart = variantsInCart.has( variantId );
					item.hidden = isInCart;

					const checkbox = item.querySelector( 'input[type="checkbox"]' );
					if ( !checkbox ) return;

					if ( isInCart ) {
						checkbox.checked = false;
						return;
					}

					if ( checkbox.disabled ) {
						checkbox.checked = true;
					}
				} );

				this._persistSelection();
				this._syncParentLiquidBlockVisibility();
			}

			_syncParentLiquidBlockVisibility () {
				const parentLiquidBlock = this.closest( '[data-block-type="liquid"]' );
				if ( !parentLiquidBlock ) return;

				const addons = Array.from( this.querySelectorAll( 'kids-product-addon' ) );
				if ( addons.length === 0 ) return;

				const allAddonsInCart = addons.every( ( addon ) => addon.hidden );
				parentLiquidBlock.hidden = allAddonsInCart;
			}

			async _fetchSections ( sectionIds ) {
				const ids = [ ...sectionIds ];
				if ( ids.length === 0 ) return {};

				const sectionEntries = await Promise.all(
					ids.map( async ( sectionId ) => {
						const html = await ( await fetch( `${ window.Shopify.routes.root }?section_id=${ encodeURIComponent( sectionId ) }` ) ).text();
						return [ sectionId, html ];
					} )
				);

				return Object.fromEntries( sectionEntries );
			}

		}
	);
}
